// Dependency-free BMP encoder.
//
// Produces a Windows BMP (BITMAPFILEHEADER + BITMAPINFOHEADER) from raw RGBA
// pixel data as produced by an HTML canvas `ImageData`. The HTML canvas 2D API
// cannot export BMP natively, so we build the byte stream by hand.
//
// Layout details that matter for a valid BMP:
//   * Multi-byte fields are little-endian.
//   * Pixel rows are stored BOTTOM-UP (last image row first).
//   * Each pixel row is padded to a multiple of 4 bytes.
//   * Colors are stored as BGR (24-bit) or BGRA (32-bit), not RGB(A).

const FILE_HEADER_SIZE = 14;
const INFO_HEADER_SIZE = 40;

export type BmpBitDepth = 24 | 32;

export type BmpEncodeResult = {
  bytes: Uint8Array<ArrayBuffer>;
  width: number;
  height: number;
  bitDepth: BmpBitDepth;
  rowSize: number;
  fileSize: number;
};

/**
 * Encode RGBA pixel data into an uncompressed BMP byte stream.
 *
 * @param data   RGBA bytes, length must be width * height * 4.
 * @param width  Image width in pixels (must be > 0).
 * @param height Image height in pixels (must be > 0).
 * @param bitDepth 24 (opaque BGR) or 32 (BGRA, keeps alpha).
 */
export function encodeBmp(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  bitDepth: BmpBitDepth = 24,
): BmpEncodeResult {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new Error('BMP encode: width and height must be positive integers.');
  }
  if (data.length < width * height * 4) {
    throw new Error('BMP encode: pixel buffer is smaller than width * height * 4.');
  }

  const bytesPerPixel = bitDepth / 8;
  // Row size padded up to the next multiple of 4 bytes.
  const rowSize = Math.floor((bitDepth * width + 31) / 32) * 4;
  const pixelArraySize = rowSize * height;
  const offBits = FILE_HEADER_SIZE + INFO_HEADER_SIZE;
  const fileSize = offBits + pixelArraySize;

  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);
  const out = new Uint8Array(buffer);

  // ---- BITMAPFILEHEADER (14 bytes) ----
  view.setUint8(0, 0x42); // 'B'
  view.setUint8(1, 0x4d); // 'M'
  view.setUint32(2, fileSize, true); // bfSize
  view.setUint16(6, 0, true); // bfReserved1
  view.setUint16(8, 0, true); // bfReserved2
  view.setUint32(10, offBits, true); // bfOffBits

  // ---- BITMAPINFOHEADER (40 bytes) ----
  view.setUint32(14, INFO_HEADER_SIZE, true); // biSize
  view.setInt32(18, width, true); // biWidth
  view.setInt32(22, height, true); // biHeight (positive => bottom-up)
  view.setUint16(26, 1, true); // biPlanes
  view.setUint16(28, bitDepth, true); // biBitCount
  view.setUint32(30, 0, true); // biCompression = BI_RGB
  view.setUint32(34, pixelArraySize, true); // biSizeImage
  view.setInt32(38, 2835, true); // biXPelsPerMeter (~72 DPI)
  view.setInt32(42, 2835, true); // biYPelsPerMeter (~72 DPI)
  view.setUint32(46, 0, true); // biClrUsed
  view.setUint32(50, 0, true); // biClrImportant

  // ---- Pixel array (bottom-up, BGR/BGRA rows padded to 4 bytes) ----
  for (let y = 0; y < height; y += 1) {
    const srcRow = y * width * 4;
    // Bottom-up: source row y lands at dest row (height - 1 - y).
    const destRow = offBits + (height - 1 - y) * rowSize;
    let dest = destRow;
    for (let x = 0; x < width; x += 1) {
      const src = srcRow + x * 4;
      const r = data[src];
      const g = data[src + 1];
      const b = data[src + 2];
      out[dest] = b;
      out[dest + 1] = g;
      out[dest + 2] = r;
      if (bytesPerPixel === 4) {
        out[dest + 3] = data[src + 3];
      }
      dest += bytesPerPixel;
    }
    // Remaining bytes in the padded row are already zero (fresh ArrayBuffer).
  }

  return { bytes: out, width, height, bitDepth, rowSize, fileSize };
}

/**
 * Sanity-check that an encoded buffer carries a well-formed BMP header for the
 * given dimensions. Returns a list of human-readable problems (empty = valid).
 * Used to VERIFY the encoder output at runtime before offering a download.
 */
export function verifyBmpHeader(
  bytes: Uint8Array,
  width: number,
  height: number,
  bitDepth: BmpBitDepth,
): string[] {
  const problems: string[] = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const rowSize = Math.floor((bitDepth * width + 31) / 32) * 4;
  const offBits = FILE_HEADER_SIZE + INFO_HEADER_SIZE;
  const expectedSize = offBits + rowSize * height;

  if (bytes.length < FILE_HEADER_SIZE + INFO_HEADER_SIZE) {
    problems.push('File is too short to contain BMP headers.');
    return problems;
  }
  if (view.getUint8(0) !== 0x42 || view.getUint8(1) !== 0x4d) {
    problems.push('Missing "BM" signature.');
  }
  if (view.getUint32(2, true) !== expectedSize) {
    problems.push('bfSize does not match the expected file size.');
  }
  if (view.getUint32(10, true) !== offBits) {
    problems.push('bfOffBits does not point at the pixel array.');
  }
  if (view.getUint32(14, true) !== INFO_HEADER_SIZE) {
    problems.push('biSize is not 40 (BITMAPINFOHEADER).');
  }
  if (view.getInt32(18, true) !== width) {
    problems.push('biWidth does not match the image width.');
  }
  if (view.getInt32(22, true) !== height) {
    problems.push('biHeight does not match the image height.');
  }
  if (view.getUint16(28, true) !== bitDepth) {
    problems.push('biBitCount does not match the requested bit depth.');
  }
  if (view.getUint32(30, true) !== 0) {
    problems.push('biCompression is not BI_RGB (0).');
  }
  if (bytes.length !== expectedSize) {
    problems.push('Total byte length does not match the computed BMP size.');
  }
  return problems;
}
